const STORAGE_PREFIX = "moodstir:event-invite:";
const inMemoryHandoff = new Map<string, string>();

type LocationLike = Pick<Location, "pathname" | "search" | "hash">;
type HistoryLike = Pick<History, "state" | "replaceState">;
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function getSessionStorageSafely(
  browser: Pick<Window, "sessionStorage">
): StorageLike | undefined {
  try {
    return browser.sessionStorage;
  } catch {
    // Some privacy policies throw while evaluating the property itself.
    return undefined;
  }
}

function eventIdFromGuestPath(pathname: string): string | null {
  const match = pathname.match(/^\/guest\/events\/([^/]+)\/?$/);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    // A malformed escaped ID should still have its bearer fragment removed.
    // The route will subsequently show its ordinary invalid-event state.
    return match[1];
  }
}

export function captureAndStripEventGuestInvite(params: {
  location: LocationLike;
  history: HistoryLike;
  storage?: StorageLike;
}): boolean {
  const eventId = eventIdFromGuestPath(params.location.pathname);
  if (!eventId || !params.location.hash) return false;

  const fragment = new URLSearchParams(params.location.hash.slice(1));
  const eventAccessToken = fragment.get("access");
  if (!eventAccessToken) return false;

  // Keep the handoff out of history state and the URL. Both can be captured by
  // analytics/session-replay tools before the guest route initializes.
  const key = `${STORAGE_PREFIX}${eventId}`;
  inMemoryHandoff.set(key, eventAccessToken);
  try {
    params.storage?.setItem(key, eventAccessToken);
  } catch {
    // Private browsing/quota policies can disable sessionStorage. The
    // same-document route can still consume the in-memory handoff.
  }
  // This is intentionally independent of storage success.
  try {
    params.history.replaceState(
      params.history.state,
      "",
      `${params.location.pathname}${params.location.search}`
    );
  } catch {
    // Retain the in-memory token even if history APIs are restricted, and use
    // the browser's fragment setter as a last-resort visible-URL scrub.
    try {
      params.location.hash = "";
    } catch {
      // There is no remaining browser API capable of rewriting this URL.
    }
  }
  return true;
}

export function consumeCapturedEventGuestInvite(params: {
  eventId: string;
  storage?: StorageLike;
}): string | null {
  const key = `${STORAGE_PREFIX}${params.eventId}`;
  let token = inMemoryHandoff.get(key) ?? null;
  inMemoryHandoff.delete(key);
  try {
    token ??= params.storage?.getItem(key) ?? null;
    params.storage?.removeItem(key);
  } catch {
    // The in-memory path remains available when storage access is blocked.
  }
  return token;
}

export function stripSensitiveUrl(url: string): string {
  try {
    const parsed = new URL(url, "https://moodstir.invalid");
    parsed.hash = "";
    return parsed.origin === "https://moodstir.invalid"
      ? `${parsed.pathname}${parsed.search}`
      : parsed.toString();
  } catch {
    return url.split("#", 1)[0];
  }
}
